import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Download } from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { usePoolComposition } from "@/hooks/capitalMarkets/useSpvPools";
import type { PoolCompositionDto, SpvPoolDto } from "@/types/spvPool";
import { downloadCsv, formatCurrency, formatPct } from "../../shared/format";

// Token-derived donut palette. Recharts needs concrete color strings; these
// reference the same CSS custom properties the design tokens expose, so no
// raw hex appears in this file.
const DONUT_COLORS = [
  "var(--gold)",
  "var(--status-info)",
  "var(--status-success)",
  "var(--status-warning)",
  "var(--gray-mid)",
  "var(--gold-shadow)",
];

interface Slice {
  name: string;
  value: number;
}

function groupBy(
  rows: PoolCompositionDto[],
  keyer: (r: PoolCompositionDto) => string | null,
): Slice[] {
  const map = new Map<string, number>();
  for (const r of rows) {
    const k = keyer(r);
    if (!k) continue;
    map.set(k, (map.get(k) ?? 0) + r.currentPrincipalBalance);
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function ConcentrationTab({ pool }: { pool: SpvPoolDto }) {
  const compositionQuery = usePoolComposition(pool.id);
  const rows = useMemo(
    () => compositionQuery.data ?? [],
    [compositionQuery.data],
  );

  // The composition DTO only carries loanId / segment fields; borrower,
  // industry and geography are not yet on the contract, so those dimensions
  // render an empty state. Credit-rating likewise relies on the loan join.
  const byBorrower = useMemo(() => groupBy(rows, (r) => r.loanId), [rows]);

  const borrowerLimit = pool.maxBorrowerConcentrationPct || 0.25;

  const handleExport = () => {
    const total = byBorrower.reduce((s, x) => s + x.value, 0) || 1;
    const out: string[][] = [["Dimension", "Bucket", "Principal", "Share %"]];
    for (const s of byBorrower) {
      out.push([
        "Borrower (loan)",
        s.name,
        String(s.value),
        ((s.value / total) * 100).toFixed(2),
      ]);
    }
    downloadCsv(
      `pool-concentration-${pool.id.slice(0, 8)}.csv`,
      out,
    );
  };

  return (
    <div className="flex flex-col gap-space-4">
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={handleExport}>
          <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <ConcentrationChart
          title="Borrower Concentration"
          slices={byBorrower}
          limit={borrowerLimit}
          loading={compositionQuery.isLoading}
        />
        <ConcentrationChart
          title="Credit Rating"
          slices={[]}
          limit={0.25}
          loading={compositionQuery.isLoading}
          emptyHint="Credit rating breakdown requires the loan join — coming with the backend report endpoint."
        />
        <ConcentrationChart
          title="Industry"
          slices={[]}
          limit={0.25}
          loading={compositionQuery.isLoading}
          emptyHint="Industry is not yet on the composition contract."
        />
        <ConcentrationChart
          title="Geography"
          slices={[]}
          limit={0.25}
          loading={compositionQuery.isLoading}
          emptyHint="Geography is not yet on the composition contract."
        />
      </div>
    </div>
  );
}

function ConcentrationChart({
  title,
  slices,
  limit,
  loading,
  emptyHint,
}: {
  title: string;
  slices: Slice[];
  limit: number;
  loading: boolean;
  emptyHint?: string;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0);

  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">{title}</h3>
      {loading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : slices.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          {emptyHint ?? "No composition data to chart yet."}
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                innerRadius={48}
                outerRadius={80}
              >
                {slices.map((s, i) => (
                  <Cell
                    key={s.name}
                    fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number) => formatCurrency(v)}
              />
            </PieChart>
          </ResponsiveContainer>
          <Table>
            <THead>
              <TR>
                <TH>Bucket</TH>
                <TH>Principal</TH>
                <TH>Share</TH>
              </TR>
            </THead>
            <TBody>
              {slices.map((s) => {
                const share = total > 0 ? s.value / total : 0;
                const breach = share > limit;
                return (
                  <TR key={s.name}>
                    <TD className={cn(breach && "text-status-error")}>
                      {s.name}
                    </TD>
                    <TD className={cn(breach && "text-status-error")}>
                      {formatCurrency(s.value)}
                    </TD>
                    <TD className={cn(breach && "text-status-error font-semibold")}>
                      {formatPct(share)}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
          <p className="text-caption text-gray-mid mt-space-2 mb-0">
            Limit: {formatPct(limit)} — rows over the limit are flagged.
          </p>
        </>
      )}
    </Card>
  );
}
